import React, { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { fetchLicenseInfo, getStoredLicenseKeyHash, LicenseInfo } from '../services/LicenseService';
import { LICENSE_POLL_INTERVAL_MS, getApiUrl } from '../config/api-config';
import { useAuth } from './AuthContext';

interface LicenseContextValue {
  licenseInfo: LicenseInfo | null;
  loading: boolean;
  error: string | null;
  lastFetchedAt: number | null;
  refresh: () => Promise<void>;
}

const LicenseContext = createContext<LicenseContextValue | null>(null);

export const LicenseProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [licenseInfo, setLicenseInfo] = useState<LicenseInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<number | null>(null);

  const inFlightRef = useRef<Promise<void> | null>(null);

  const refresh = useCallback(async () => {
    if (inFlightRef.current) {
      return inFlightRef.current;
    }
    const promise = (async () => {
      try {
        setError(null);
        const info = await fetchLicenseInfo();
        setLicenseInfo(info);
        setLastFetchedAt(Date.now());
        if (!info) {
          setError('Failed to fetch license info');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    })();
    inFlightRef.current = promise;
    try {
      await promise;
    } finally {
      inFlightRef.current = null;
    }
  }, []);

  // Initial fetch on mount
  useEffect(() => {
    refresh();
  }, [refresh]);

  // In-tab event bus: any code path that dispatches `license-changed` triggers a refresh
  useEffect(() => {
    const handler = () => { refresh(); };
    window.addEventListener('license-changed', handler);
    return () => window.removeEventListener('license-changed', handler);
  }, [refresh]);

  // Auth transitions change what license-info means for this user, and until
  // now only license-changed events and the poll triggered a refetch — so
  // right after sign-in, consumers (Getting Started guide, header tier badge)
  // could act on stale pre-login data for up to the poll interval. Refetch on
  // every sign-in/sign-out flip. (LicenseProvider sits inside AuthProvider.)
  const { state: authState } = useAuth();
  useEffect(() => {
    refresh();
  }, [authState.isAuthenticated, refresh]);

  // Tab visibility: when the user returns to this tab (e.g., after cancelling a trial in
  // the LS portal tab), refetch immediately so the UI catches up before the next poll.
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible') {
        refresh();
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [refresh]);

  // Polling backstop
  useEffect(() => {
    const id = setInterval(refresh, LICENSE_POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  // Server-Sent Events bridge.
  // The PFB backend pushes `license-changed` events via SSE when the LS webhook fires
  // (license expiration, trial cancellation, tier change). The frontend reacts immediately
  // instead of waiting for the next poll or a tab-visibility change.
  //
  // NOTE: SSE requires a live HTTP connection to the PFB backend. In future air-gapped
  // standalone PFB installs (no external internet, no LS connectivity), there is nothing
  // for the SSE endpoint to forward — events would only fire from in-tab actions. The
  // visibilitychange handler and 5-min poll above remain effective in that scenario.
  useEffect(() => {
    let source: EventSource | null = null;
    let cancelled = false;
    let retryTimeout: ReturnType<typeof setTimeout> | null = null;
    let retryDelayMs = 1000;

    const connect = () => {
      if (cancelled) return;
      try {
        // EventSource cannot send custom headers, so the licenseKeyHash (which the
        // backend uses to scope events to this tenant) is appended as a query param.
        // The hash is a tenant identifier, not a secret — it's already on every
        // license-aware request as the X-License-Key-Hash header.
        const hash = getStoredLicenseKeyHash();
        const url = hash
          ? `${getApiUrl('/api/pdf/license/events')}?h=${encodeURIComponent(hash)}`
          : getApiUrl('/api/pdf/license/events');
        source = new EventSource(url, { withCredentials: true });

        source.addEventListener('license-changed', () => {
          refresh();
        });

        source.onopen = () => {
          retryDelayMs = 1000; // reset backoff on success
        };

        source.onerror = () => {
          // EventSource auto-reconnects on most transient errors; close and reschedule
          // explicitly to apply our own bounded exponential backoff.
          if (source) {
            source.close();
            source = null;
          }
          if (!cancelled) {
            retryTimeout = setTimeout(connect, retryDelayMs);
            retryDelayMs = Math.min(retryDelayMs * 2, 30_000);
          }
        };
      } catch (err) {
        console.warn('Failed to open license SSE stream:', err);
      }
    };

    connect();

    return () => {
      cancelled = true;
      if (retryTimeout) clearTimeout(retryTimeout);
      if (source) source.close();
    };
  }, [refresh]);

  return (
    <LicenseContext.Provider value={{ licenseInfo, loading, error, lastFetchedAt, refresh }}>
      {children}
    </LicenseContext.Provider>
  );
};

export const useLicense = (): LicenseContextValue => {
  const ctx = useContext(LicenseContext);
  if (!ctx) {
    throw new Error('useLicense must be used within a LicenseProvider');
  }
  return ctx;
};
